import { useDisclosure } from '@withjoy/joykit';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog.context';
import { useAuth } from '@shared/components/AuthProvider';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useUserProfile } from '@shared/utils/useUserProfile';
import { useGetEventCTAButtonsDataQuery } from '../CtaButtons/CtaButtons.graphql.generated';
import { DesignLayoutType } from '@graphql/generated-base';
import { useLogoutEventSessionMutation } from '@graphql/queries/eventSession.graphql.generated';
import { performFullPageRedirect } from '@shared/utils/navigation';
import { useHistory } from '@react-router';
import { useTranslation } from '@shared/core';
import { useCallback, useMemo } from 'react';
import { addError } from '@shared/utils/logger';
import type { NavbarEventPropsInterface } from '../../layouts/layout.types';
import { notNullOrUndefined } from '@shared/utils/notNullOrUndefined';
import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { isRsvpEffectivelyEnabled } from '@apps/guest/packages/layout-engine/common/utils/legacyRsvpQuestions';
import type { CtaButtonsSessionData } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.types';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { useRsvpDeadlineGuard } from '../../common/utils/useRsvpDeadlineGuard';

interface EventMenuControllerProps
  extends Readonly<{
    eventHandle: string;
    onClose?: () => void;
    handleMenuClicked?: (title: string) => void;
    handleMenuOpenedChange?: (opened: boolean) => void;
    eventProps: NavbarEventPropsInterface;
  }> {}

export const useEventMenuController = ({ eventHandle, handleMenuClicked, handleMenuOpenedChange, eventProps, onClose: externalOnClose }: EventMenuControllerProps) => {
  const { isOpen, onClose: internalOnClose, onOpen } = useDisclosure();

  const onClose = externalOnClose ?? internalOnClose;
  const { handleUnlockDialogOpen } = useUnlockDialogContext();
  const { isAdmin } = useEventUserRole();
  const { getButtonsVisibility, getUnlockButtonText, getRSVPButtonText, rsvpButtonCustomLabel } = useCtaButtons();
  const history = useHistory();
  const { t } = useTranslation('layoutEngine');
  const buttonNotYouText = t('components', 'eventCta', 'buttonNotYouText')();
  const queryString = useQueryString();
  const disableLegacyRsvp = useProFeature(EventFeature.disableLegacyRsvp);

  let profileFirstName, profileLastName, profileEmail;

  const {
    currentUser: { profile },
    loginManager,
  } = useAuth();

  if (profile) {
    ({ firstName: profileFirstName, lastName: profileLastName, email: profileEmail } = profile);
  }

  const profileFullName = [profileFirstName, profileLastName].filter(name => !!name).join(' ');
  const profileInitials = useUserProfile().initials;

  const [logoutEvent] = useLogoutEventSessionMutation({ variables: { eventId: eventProps?.eventId } });
  // Marking this query as `ssr: false` because any RSVP data query is proxied through app-server-api -- which takes long to resolve.
  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventHandle || '' },
    batchMode: 'fast',
    ssr: false,
  });

  const { guestFullName, guestInitials, guestEmail, layout, rsvpIsEnabled, hasUnlocked, pages } = useMemo(() => {
    const activeSession = sessionData?.eventByName?.activeSession;
    const person = activeSession?.person;
    const eventCTA = activeSession?.eventCTA;

    const guestFullName = person?.fullName || '';
    const guestInitials = person?.avatarName || '';
    const guestEmail = person?.email || '';
    const hasUnlocked = activeSession?.hasUnlocked || false;
    const rsvpIsEnabled = !disableLegacyRsvp && isRsvpEffectivelyEnabled(eventCTA?.rsvpIsEnabled, sessionData?.eventByName?.hasEnabledLegacyRsvpQuestions);
    const isRsvpRequiresPassword = eventCTA?.requiresPassword || false;

    const layout = eventProps?.layoutType || DesignLayoutType.aloha;
    const origPages = eventProps?.pages ?? [];

    const pages = origPages
      .map(page => {
        if (page.type === 'tidbits' && layout === DesignLayoutType.brannan) {
          return null;
        }
        return page;
      })
      .filter(notNullOrUndefined);

    return {
      guestFullName,
      guestInitials,
      guestEmail,
      layout,
      rsvpIsEnabled,
      isRsvpRequiresPassword,
      hasUnlocked,
      pages,
    };
  }, [disableLegacyRsvp, eventProps, sessionData]);

  const handleClickItemMenu = useCallback(
    (title: string) => {
      handleMenuClicked?.(title);
      onClose();
    },
    [handleMenuClicked, onClose],
  );

  const rsvpHandle = useCallback(() => {
    handleMenuClicked?.('rsvp');
    performFullPageRedirect(`/${eventHandle}/rsvp${queryString || ''}`);
  }, [handleMenuClicked, eventHandle, queryString]);

  const handleOnOpen = useCallback(() => {
    onOpen();
    handleMenuOpenedChange?.(true);
  }, [onOpen, handleMenuOpenedChange]);

  const handleOnClose = useCallback(() => {
    onClose();
    handleMenuOpenedChange?.(false);
  }, [onClose, handleMenuOpenedChange]);

  const onProfileHandle = useCallback(() => {
    handleMenuClicked?.('Account');
    history.push('/account/events');
  }, [handleMenuClicked, history]);

  const logoutHandle = async () => {
    try {
      onClose();
      if (!profile) {
        await logoutEvent({ refetchQueries: () => ['GetEventSession', 'GetGuestSiteSchedule', 'GetEventCTAButtonsData'] });
      } else {
        //This is a temporary solution until the useAuth().provideLogout is fixed.
        // Problem ticket https://withjoy.atlassian.net/browse/WEB-1674
        await loginManager.authService.clearToken();
        await logoutEvent();
        window.location.reload();
      }
    } catch (error) {
      addError(error, { context: 'EventMenu', operation: 'logoutHandle' });
    }
  };

  const event: CtaButtonsSessionData = sessionData?.eventByName ?? {};
  const { activeSession, settings, welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, customPages } = event;
  // Include customPages so filterPages sees password-protected custom pages (matches ActionBar/CtaButtons).
  // Without them, a blocked custom page is invisible here and the specificGuests swap wrongly skips the password gate.
  const initialPages = [welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, ...(customPages ?? [])];
  const { label: rsvpLabel, rsvpAttendanceStatus } = activeSession?.eventCTA || {};
  const rsvpIsPasswordProtected = rsvpPage?.private;
  const hasIdentity = activeSession?.hasIdentity;
  const hasSpecificGuestsPages = activeSession?.eventCTA?.hasSpecificGuestsPages;
  // isSpecificGuestsOnlyUnlock is true only when the specificGuests swap surfaced the unlock button
  // (no password gate); password/blocked-pages unlocks return it false so they keep the password form.
  const {
    shouldDisplayUnlockButton,
    shouldDisplayRSVPButton: shouldDisplayRSVPButtonBase,
    isSpecificGuestsOnlyUnlock,
  } = getButtonsVisibility({
    pages: initialPages,
    welcomePage,
    schedulePage,
    rsvpIsEnabled,
    rsvpIsPasswordProtected,
    hasUnlocked,
    hasIdentity,
    hasSpecificGuestsPages,
    requirePasswordForEventDate: settings?.requirePasswordForEventDate,
    requirePasswordForEventLocation: settings?.requirePasswordForEventLocation,
    customPages,
  });

  const unlockDialogHandle = useCallback(() => {
    handleMenuClicked?.('unlockDialog');
    onClose();
    handleUnlockDialogOpen(
      // getButtonsVisibility's isSpecificGuestsOnlyUnlock maps to the dialog's isSpecificGuestsUnlock option.
      isSpecificGuestsOnlyUnlock
        ? { displayFindInvite: true, skipPasswordGate: true, successBehavior: 'reloadInPlace', isSpecificGuestsUnlock: true }
        : { displayFindInvite: true },
    );
  }, [handleUnlockDialogOpen, onClose, handleMenuClicked, isSpecificGuestsOnlyUnlock]);

  const { isRsvpClosedByDeadline } = useRsvpDeadlineGuard();
  // The specificGuests swap is an identify action, not a schedule unlock — never label it "Unlock Your
  // Schedule" (schedule-page only), even for an unlocked-but-unidentified guest.
  const unlockButtonText = getUnlockButtonText(hasUnlocked && !isSpecificGuestsOnlyUnlock);
  const rsvpButtonText = rsvpButtonCustomLabel || getRSVPButtonText(rsvpAttendanceStatus, rsvpLabel);

  return {
    onOpen: handleOnOpen,
    isOpen,
    onClose: handleOnClose,
    name: profile ? profileFullName : guestFullName,
    layout,
    email: profile ? profileEmail : guestEmail,
    initials: profile ? profileInitials : guestInitials,
    isAdmin,
    unlockDialogHandle,
    rsvpHandle,
    shouldDisplayRSVPButton: shouldDisplayRSVPButtonBase && !isRsvpClosedByDeadline,
    shouldDisplayUnlockButton,
    pages,
    isProfile: !!profile,
    onProfileHandle,
    logoutHandle,
    buttonNotYouText,
    handleClickItemMenu,
    unlockButtonText,
    rsvpButtonText,
  };
};
